import {
  Box,
  Grid,
  Skeleton,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { useCurrentPageUserId } from '../../../../hooks/useCurrentPageUserId';
import ArticleLayout from '../../../../layouts/ArticleLayout';
import TabEducationEntry from './shared-components/TabEducationEntry';
import { getFullMonthNameAndDay } from '../../../../utils/date-time-formats.ts/formatDate';
import SquareIconButton from '../../../../components/MUIComponents/SquareIconButton';
import EditIcon from '../../../../components/Icons/EditIcon';
import { useNavigate } from 'react-router-dom';
import { IUser } from '../../../../interfaces/user.interface';

interface AboutTabProps {
  user?: IUser;
}

const NO_DATA = 'No information';

const AboutTab = ({ user }: AboutTabProps) => {
  const { isOwnPage } = useCurrentPageUserId();
  const navigate = useNavigate();

  // Use unified IUser from /me or /:userId (includes EDUCATIONS via PROFILE_PAGE_INCLUDES).
  const isLoading = !user;

  const handleAboutButtonClick = () => {
    navigate('/profile#personal-info');
  };

  const handleEducationButtonClick = () => {
    navigate('/profile#educations', { state: { moveToEducationBlock: true } });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      rowGap={{ xs: '16px', xl: '22px' }}
    >
      {isLoading && (
        <Skeleton
          variant="rounded"
          sx={{
            width: '100%',
            minHeight: '370px',
          }}
        />
      )}
      {!isLoading && user && (
        <ArticleLayout
          className="about-article"
          rowGap={8}
          display="flex"
          flexDirection="column"
        >
          <>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h3" textTransform="capitalize">
                about
              </Typography>
              {isOwnPage && (
                <SquareIconButton onClick={handleAboutButtonClick}>
                  <EditIcon />
                </SquareIconButton>
              )}
            </Stack>

            <Grid container rowGap="28px" sx={gridContainerStyles}>
              <Grid item display="flex" flexDirection="row">
                <Typography className="label" variant="caption">
                  Full Name:
                </Typography>
                <Typography className="plain-text" variant="body3">
                  {user?.firstName
                    ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}`
                    : NO_DATA}
                </Typography>
              </Grid>
              {user?.isBirthdateVisible && (
                <Grid item display="flex" flexDirection="row">
                  <Typography className="label" variant="caption">
                    Birthday:
                  </Typography>
                  <Typography className="plain-text" variant="body3">
                    {getFullMonthNameAndDay(user?.birthdate ?? '') ?? NO_DATA}
                  </Typography>
                </Grid>
              )}
              <Grid item display="flex" flexDirection="row">
                <Typography className="label" variant="caption">
                  My Bio:
                </Typography>
                <Typography className="plain-text" variant="body3">
                  {user?.bio || NO_DATA}
                </Typography>
              </Grid>
              <Grid item display="flex" flexDirection="row">
                <Typography className="label" variant="caption">
                  Country:
                </Typography>
                <Typography className="plain-text" variant="body3">
                  {user?.country || NO_DATA}
                </Typography>
              </Grid>
              <Grid item display="flex" flexDirection="row">
                <Typography className="label" variant="caption">
                  City:
                </Typography>
                <Typography className="plain-text" variant="body3">
                  {user?.city || NO_DATA}
                </Typography>
              </Grid>
            </Grid>
          </>
        </ArticleLayout>
      )}

      {isLoading && (
        <Skeleton
          variant="rounded"
          sx={{
            width: '100%',
            minHeight: '270px',
          }}
        />
      )}

      {!isLoading && user && (
        <ArticleLayout
          className="education-article"
          rowGap={8}
          display="flex"
          flexDirection="column"
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h3" textTransform="capitalize">
              education
            </Typography>
            {isOwnPage && (
              <SquareIconButton onClick={handleEducationButtonClick}>
                <EditIcon />
              </SquareIconButton>
            )}
          </Stack>

          {user.educations?.length ? (
            user.educations.map((education) => (
              <TabEducationEntry key={education.id} education={education} />
            ))
          ) : (
            <TabEducationEntry />
          )}
        </ArticleLayout>
      )}
    </Box>
  );
};

export default AboutTab;

export const gridContainerStyles: SxProps<Theme> = (theme) => ({
  '& .label': {
    width: 165,
    flexShrink: 0,
  },
  '& .plain-text': {
    fontWeight: 400,
    lineHeight: '140%',
    color: theme.palette.text.primary,

    textAlign: 'left',
    flexGrow: '1',
    wordWrap: 'break-word',
    whiteSpace: 'normal',
    minWidth: 0,
  },
  '& .MuiGrid-item': {
    width: '100%',
  },
});
